import { Building2, Search, Loader2, Edit, Trash2, Eye, Key, Home } from "lucide-react"
import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Layout } from "@/components/Layout"
import { useUnits } from "@/hooks/useUnits"
import { useAuth } from "@/hooks/useAuth"
import NovaUnidadeModal from "@/components/NovaUnidadeModal"
import EditarUnidadeModal from "@/components/EditarUnidadeModal"
import DetalhesUnidadeModal from "@/components/DetalhesUnidadeModal"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { useQueryClient } from "@tanstack/react-query"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

const Unidades = () => {
  const { data: unidades, isLoading, error } = useUnits()
  const { profile } = useAuth()
  const [searchTerm, setSearchTerm] = useState("")
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const canManageUnits = profile?.role === 'sindico' || profile?.role === 'administracao' || profile?.role === 'superadmin'

  const filteredUnidades = unidades?.filter(unidade => {
    const searchLower = searchTerm.toLowerCase()
    const unitNumber = unidade.building ? `${unidade.building}-${unidade.number}` : unidade.number
    const ownerName = unidade.owner?.full_name || ""
    
    return unitNumber.toLowerCase().includes(searchLower) ||
           ownerName.toLowerCase().includes(searchLower)
  }) || []

  const handleDeleteUnit = async (unitId: string) => {
    setDeletingId(unitId)
    try {
      console.log('Deletando unidade:', unitId)

      const { error } = await supabase
        .from("units")
        .delete()
        .eq("id", unitId)

      if (error) throw error

      toast({
        title: "Sucesso!",
        description: "Unidade deletada com sucesso.",
      })

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ["units"] })
      queryClient.invalidateQueries({ queryKey: ["stats"] })
    } catch (error: any) {
      console.error("Erro ao deletar unidade:", error)
      toast({
        title: "Erro",
        description: error.message || "Erro ao deletar unidade.",
        variant: "destructive",
      })
    } finally {
      setDeletingId(null)
    }
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center text-red-600">
            Erro ao carregar unidades: {error.message}
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-ita-black">Unidades</h1>
            <p className="text-gray-600">Gerencie as unidades do condomínio</p>
          </div>
          {canManageUnits && <NovaUnidadeModal />}
        </div>

        <div className="flex items-center space-x-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input 
              placeholder="Buscar unidades..." 
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {filteredUnidades.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {searchTerm ? "Nenhuma unidade encontrada" : "Nenhuma unidade cadastrada"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUnidades.map((unidade) => (
              <Card key={unidade.id} className="ita-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold">
                      {unidade.building ? `${unidade.building}-${unidade.number}` : unidade.number}
                    </CardTitle>
                    <Badge variant={
                      unidade.occupancy_status === 'both' ? 'default' :
                      unidade.occupancy_status === 'owner_occupied' ? 'secondary' :
                      unidade.occupancy_status === 'tenant_occupied' ? 'outline' :
                      'destructive'
                    }>
                      {unidade.occupancy_status === 'both' ? 'Prop. + Loc.' :
                       unidade.occupancy_status === 'owner_occupied' ? 'Proprietário' :
                       unidade.occupancy_status === 'tenant_occupied' ? 'Locatário' :
                       'Vago'}
                    </Badge>
                  </div>
                  <CardDescription>
                    Unidade {unidade.floor ? `• Andar ${unidade.floor}` : ""}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Proprietário */}
                    <div className="flex items-center space-x-2">
                      <Home className="h-4 w-4 text-ita-blue" />
                      <div>
                        <p className="text-xs text-gray-500">Proprietário</p>
                        <p className="text-sm font-medium">
                          {unidade.owner?.full_name || unidade.unit_details?.owner_name || "Não informado"}
                        </p>
                      </div>
                    </div>

                    {/* Locatário */}
                    {(unidade.tenant || unidade.unit_details?.tenant_name) && (
                      <div className="flex items-center space-x-2">
                        <Key className="h-4 w-4 text-orange-500" />
                        <div>
                          <p className="text-xs text-gray-500">Locatário</p>
                          <p className="text-sm font-medium">
                            {unidade.tenant?.full_name || unidade.unit_details?.tenant_name}
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <DetalhesUnidadeModal unit={unidade}>
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4 mr-2" />
                          Detalhes
                        </Button>
                      </DetalhesUnidadeModal>
                      {canManageUnits && (
                        <div className="flex items-center space-x-2">
                          <EditarUnidadeModal unit={unidade}>
                            <Button variant="outline" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </EditarUnidadeModal>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja deletar a unidade {unidade.building ? `${unidade.building}-${unidade.number}` : unidade.number}? 
                                  Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteUnit(unidade.id)}
                                  disabled={deletingId === unidade.id}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  {deletingId === unidade.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                  Deletar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}

export default Unidades
